import React, { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Text,
  useToast,
  useColorModeValue,
  InputGroup,
  InputRightAddon,
  HStack,
  Stack,
  Tag,
  Collapse,
  VStack,
  ScaleFade,
  useBreakpointValue,
} from "@chakra-ui/react";
import {
  checkEmailAvailability,
  sendEmailVerificationCode,
  verifyEmailVerificationCode,
  signup,
} from "services/auth";

const EMAIL_DOMAIN = "xisnd.com";

export default function SignUp() {
  const textColor = useColorModeValue("#176fbd", "white");
  const textColorBrand = useColorModeValue("#176fbd", "white");
  const stepInstructionBg = useColorModeValue("gray.50", "gray.700");
  const inputAddonBg = useColorModeValue("gray.50", "gray.700");
  const isMobile = useBreakpointValue({ base: true, md: false });

  const toast = useToast();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [emailLocalPart, setEmailLocalPart] = useState("");
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const [emailStatus, setEmailStatus] = useState({ status: "idle", message: "" });
  const [isEmailAvailable, setIsEmailAvailable] = useState(false);
  const [checkingEmail, setCheckingEmail] = useState(false);

  const [otpToken, setOtpToken] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [otpStatus, setOtpStatus] = useState({ status: "idle", message: "" });
  const [otpSectionOpen, setOtpSectionOpen] = useState(false);
  const [passwordSectionOpen, setPasswordSectionOpen] = useState(false);
  const [actionSectionOpen, setActionSectionOpen] = useState(false);
  const [isSignupSubmitted, setIsSignupSubmitted] = useState(false);

  const normalizedLocal = emailLocalPart.trim().toLowerCase();
  const fullEmail = normalizedLocal ? `${normalizedLocal}@${EMAIL_DOMAIN}` : "";

  useEffect(() => {
    setIsEmailAvailable(false);
    setEmailStatus({ status: "idle", message: "" });
    setOtpToken("");
    setOtpCode("");
    setIsOtpVerified(false);
    setOtpStatus({ status: "idle", message: "" });
  }, [normalizedLocal]);

  const handleEmailChange = (value) => {
    const cleaned = value.replace(/@/g, "").replace(/\s+/g, "").toLowerCase();
    setEmailLocalPart(cleaned);
  };

  const handleCheckEmail = async () => {
    if (!normalizedLocal) {
      toast({ status: "warning", title: "이메일 아이디를 입력하세요." });
      return;
    }
    const email = `${normalizedLocal}@${EMAIL_DOMAIN}`;
    try {
      setCheckingEmail(true);
      const { exists } = await checkEmailAvailability(email);
      if (exists) {
        setEmailStatus({ status: "error", message: "이미 가입된 이메일입니다." });
        setIsEmailAvailable(false);
      } else {
        setEmailStatus({ status: "success", message: "사용 가능한 이메일입니다." });
        setIsEmailAvailable(true);
      }
    } catch (e) {
      setEmailStatus({
        status: "error",
        message: String(e?.response?.data?.detail || e?.message || "이메일 확인에 실패했습니다."),
      });
      setIsEmailAvailable(false);
    } finally {
      setCheckingEmail(false);
    }
  };

  const handleSendCode = async () => {
    if (!isEmailAvailable) {
      toast({ status: "warning", title: "중복확인을 먼저 완료하세요." });
      return;
    }
    try {
      setSendingCode(true);
      const { otpToken } = await sendEmailVerificationCode(fullEmail);
      setOtpToken(otpToken);
      setOtpStatus({ status: "sent", message: "인증코드가 발송되었습니다." });
      setIsOtpVerified(false);
      setOtpCode("");
    } catch (e) {
      setOtpStatus({
        status: "error",
        message: String(e?.response?.data?.detail || e?.message || "인증코드 발송에 실패했습니다."),
      });
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!otpToken) {
      toast({ status: "warning", title: "먼저 인증코드를 발송하세요." });
      return;
    }
    if (!otpCode) {
      toast({ status: "warning", title: "인증코드를 입력하세요." });
      return;
    }
    try {
      setVerifyingCode(true);
      await verifyEmailVerificationCode({ email: fullEmail, code: otpCode, otpToken });
      setIsOtpVerified(true);
      setOtpStatus({ status: "success", message: "이메일 인증이 완료되었습니다." });
    } catch (e) {
      setIsOtpVerified(false);
      setOtpStatus({
        status: "error",
        message: String(e?.response?.data?.detail || e?.message || "인증코드가 일치하지 않습니다."),
      });
    } finally {
      setVerifyingCode(false);
    }
  };

  const handleSubmit = async () => {
    if (!name || !normalizedLocal || !password || !passwordConfirm) {
      toast({ status: "warning", title: "필수 입력값을 모두 채워주세요." });
      return;
    }
    if (!isOtpVerified) {
      toast({ status: "warning", title: "이메일 인증을 완료해야 합니다." });
      return;
    }
    setLoading(true);
    try {
      await signup({
        name,
        email: fullEmail,
        password,
        password2: passwordConfirm,
      });
      toast({
        status: "success",
        title: "가입 신청 완료",
        description: "관리자 승인 후 로그인할 수 있습니다.",
      });
      setIsSignupSubmitted(true);
      navigate("/auth/sign-in/default");
    } catch (e) {
      toast({
        status: "error",
        title: "회원가입 실패",
        description: String(e?.response?.data?.detail || e?.message),
      });
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (state) => {
    if (state === "success") return "green.500";
    if (state === "error") return "red.500";
    if (state === "sent") return "blue.500";
    return undefined;
  };

  const getTagScheme = () => {
    if (isOtpVerified) return "green";
    if (otpStatus.status === "error") return "red";
    if (otpStatus.status === "sent") return "blue";
    return "gray";
  };

  const getTagLabel = () => {
    if (isOtpVerified) return "인증 완료";
    if (otpStatus.status === "error") return "재시도 필요";
    if (otpStatus.status === "sent") return "인증코드 발송됨";
    return "인증 상태";
  };

  useEffect(() => {
    setOtpSectionOpen(isEmailAvailable);
    if (!isEmailAvailable) {
      setPasswordSectionOpen(false);
      setActionSectionOpen(false);
      setIsOtpVerified(false);
      setOtpToken("");
      setOtpCode("");
      setOtpStatus({ status: "idle", message: "" });
    }
  }, [isEmailAvailable]);

  useEffect(() => {
    setPasswordSectionOpen(isOtpVerified);
    setActionSectionOpen(isOtpVerified);
  }, [isOtpVerified]);

  const isStageOneActive = !isSignupSubmitted;
  const isStageTwoActive = isSignupSubmitted;
  const steps = [
    {
      id: 1,
      title: "회원정보",
      description: isMobile ? "정보 & 인증" : "회원 정보 및 인증 확인",
      active: isStageOneActive,
    },
    {
      id: 2,
      title: "가입안내",
      description: isMobile ? "관리자 승인 대기" : "관리자 승인 확인",
      active: isStageTwoActive,
    },
  ];
  const canSubmit = isOtpVerified && !loading;

  return (
    <Flex direction="column" w="100%" maxW="540px">
      <Stack spacing={4}>
        <Box
          bg={useColorModeValue("white", "gray.800")}
          borderRadius="2xl"
          border="1px solid"
          borderColor={useColorModeValue("gray.200", "gray.600")}
          shadow="md"
          px={{ base: 4, md: 6 }}
          py={4}
        >
          <HStack spacing={{ base: 4, md: 8 }} justify="space-between" align="center">
            {steps.map((step) => (
              <VStack
                key={step.id}
                spacing={1}
                align="center"
                flex={1}
                textAlign="center"
              >
                <Box
                  w="40px"
                  h="40px"
                  borderRadius="full"
                  border="1px solid"
                  borderColor={step.active ? "blue.500" : "gray.300"}
                  bg={step.active ? "blue.50" : "transparent"}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Text fontWeight="600" fontSize="md" color={step.active ? "blue.600" : "gray.500"}>
                    {step.id}
                  </Text>
                </Box>
                <Text fontWeight="600" color={step.active ? "blue.600" : "gray.700"}>
                  {step.title}
                </Text>
                <Text fontSize="xs" color="gray.500">
                  {step.description}
                </Text>
              </VStack>
            ))}
          </HStack>
        </Box>
        {!isSignupSubmitted && (
          <Collapse in={!isSignupSubmitted}>
            <Box
              p={3}
              border="1px dashed"
              borderColor="gray.200"
              borderRadius="md"
              bg={stepInstructionBg}
            >
              <Text fontSize="sm" color="gray.600">
                {isMobile
                  ? "이메일 인증 완료 후 비밀번호 입력으로 넘어갑니다."
                  : "이메일 인증을 완료하고 비밀번호를 입력하세요."}
              </Text>
            </Box>
          </Collapse>
        )}
        <FormControl>
          <FormLabel color={textColor}>이름</FormLabel>
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </FormControl>

        <FormControl>
          <FormLabel color={textColor}>이메일</FormLabel>
          <InputGroup mb={3}>
            <Input
              flex={1}
              value={emailLocalPart}
              onChange={(e) => handleEmailChange(e.target.value)}
              placeholder="아이디를 입력하세요."
              borderRightRadius="0"
            />
            <InputRightAddon bg={inputAddonBg} color="gray.500">
              @{EMAIL_DOMAIN}
            </InputRightAddon>
          </InputGroup>
          <HStack spacing={2} w="100%">
            <Button
              flex={1}
              size="sm"
              onClick={handleCheckEmail}
              isLoading={checkingEmail}
              fontWeight="600"
              h="44px"
              px={4}
              borderRadius="xl"
              bgGradient="linear(to-r, #4f46e5, #1d4ed8)"
              color="white"
              _hover={{ bgGradient: "linear(to-r, #4338ca, #1e40af)" }}
              boxShadow="0 12px 20px rgba(37, 99, 235, 0.2)"
            >
              중복확인
            </Button>
            <Button
              flex={1}
              size="sm"
              onClick={handleSendCode}
              isLoading={sendingCode}
              isDisabled={!isEmailAvailable}
              fontWeight="600"
              h="44px"
              px={4}
              borderRadius="xl"
              bgGradient="linear(to-r, #4f46e5, #1d4ed8)"
              color="white"
              _hover={{ bgGradient: isEmailAvailable ? "linear(to-r, #4338ca, #1e40af)" : "linear(to-r, #4f46e5, #1d4ed8)" }}
              _disabled={{
                bgGradient: "linear(to-r, #4f46e5, #1d4ed8)",
                opacity: 0.6,
                cursor: "not-allowed",
              }}
              boxShadow="0 12px 20px rgba(37, 99, 235, 0.2)"
            >
              인증코드 발송
            </Button>
          </HStack>
          {emailStatus.message && (
            <Text mt={1} fontSize="sm" color={getStatusColor(emailStatus.status)}>
              {emailStatus.message}
            </Text>
          )}
        </FormControl>

        <Collapse in={otpSectionOpen} animateOpacity>
          <ScaleFade initialScale={0.95} in={otpSectionOpen}>
            <FormControl>
              <FormLabel color={textColor}>이메일 인증 코드</FormLabel>
              <HStack spacing={3}>
                <Input
                  flex={1}
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))}
                  placeholder="인증코드를 입력하세요."
                  isDisabled={!otpToken}
                />
                <Button
                  minW="150px"
                  h="52px"
                  px={6}
                  size="md"
                  borderRadius="md"
                  fontWeight="600"
                  textTransform="none"
                  onClick={handleVerifyCode}
                  isLoading={verifyingCode}
                  isDisabled={!otpToken}
                  colorScheme="blue"
                >
                  인증코드 확인
                </Button>
              </HStack>
              {otpStatus.message && (
                <HStack mt={1} spacing={2}>
                  <Tag size="sm" colorScheme={getTagScheme()}>
                    {getTagLabel()}
                  </Tag>
                  <Text fontSize="sm" color={getStatusColor(otpStatus.status)}>
                    {otpStatus.message}
                  </Text>
                </HStack>
              )}
            </FormControl>
          </ScaleFade>
        </Collapse>

        <Collapse in={passwordSectionOpen} animateOpacity>
          <ScaleFade initialScale={0.95} in={passwordSectionOpen}>
            <FormControl>
              <FormLabel color={textColor}>비밀번호</FormLabel>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
            </FormControl>
            <FormControl>
              <FormLabel color={textColor}>비밀번호 확인</FormLabel>
              <Input
                type="password"
                value={passwordConfirm}
                onChange={(e) => setPasswordConfirm(e.target.value)}
              />
            </FormControl>
          </ScaleFade>
        </Collapse>

        <Collapse in={actionSectionOpen} animateOpacity>
          <ScaleFade initialScale={0.95} in={actionSectionOpen}>
            <Button
              variant="brand"
              w="100%"
              h="52px"
              isLoading={loading}
              onClick={handleSubmit}
              background="#176fbd"
              color="white"
              fontWeight="600"
              isDisabled={!isOtpVerified || loading}
              _hover={
                canSubmit
                  ? { background: "#0f53a1", boxShadow: "lg" }
                  : { background: "#176fbd" }
              }
              _disabled={{
                background: "#176fbd",
                opacity: 0.85,
                cursor: "not-allowed",
                _hover: { background: "#176fbd" },
              }}
            >
              회원가입
            </Button>
          </ScaleFade>
        </Collapse>

        <Collapse in={isSignupSubmitted} animateOpacity>
          <Box
            mt={4}
            p={4}
            borderRadius="md"
            border="1px solid"
            borderColor="blue.200"
            bg={stepInstructionBg}
          >
            <Text fontWeight="600">2단계: 회원가입 신청이 완료되었습니다.</Text>
            <Text fontSize="sm" color="gray.600">
              관리자 승인이 완료되면 자동으로 로그인할 수 있습니다. 승인/반려 처리는 사용자 관리 메뉴에서 진행되며, 반려 시 사유와 함께 다시 안내드립니다.
            </Text>
          </Box>
        </Collapse>
      </Stack>

      <Text mt="16px" fontSize="sm">
        이미 계정이 있나요?{" "}
        <NavLink to="/auth/sign-in/default">
          <Text as="span" color={textColorBrand} fontWeight="600">
            로그인
          </Text>
        </NavLink>
      </Text>
    </Flex>
  );
}
